import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowRight, Wrench, Buildings } from "@phosphor-icons/react";
import { IMAGES, CONTACT } from "@/data/content";

const LINES = ["Built Right.", "Diagnosed Precisely."];

const lineVariant = {
  hidden: { y: "110%" },
  show: (i) => ({ y: "0%", transition: { duration: 0.9, delay: 0.35 + i * 0.14, ease: [0.22, 1, 0.36, 1] } }),
};

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const opacity = useTransform(scrollYProgress, [0, 0.8], [1, 0]);
  const scale = useTransform(scrollYProgress, [0, 1], [1, 1.12]);

  return (
    <section ref={ref} data-testid="hero" className="relative h-screen min-h-[720px] w-full overflow-hidden">
      <motion.div style={{ y, scale }} className="absolute inset-0">
        <img src={IMAGES.hero} alt="Commercial rooftop HVAC equipment at dusk" className="h-full w-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-b from-ink/70 via-ink/55 to-ink" />
        <div className="absolute inset-0 bg-gradient-to-r from-ink/80 via-transparent to-transparent" />
      </motion.div>

      <div className="blueprint-grid absolute inset-0 opacity-30" />

      {/* airflow lines */}
      <div className="pointer-events-none absolute inset-0 overflow-hidden">
        {[20, 42, 63, 82].map((top, i) => (
          <div key={top} className="airflow-line absolute h-px w-24 bg-gradient-to-r from-transparent via-brand-cyan/60 to-transparent"
            style={{ top: `${top}%`, animationDuration: `${7 + i * 1.5}s`, animationDelay: `${i * 1.2}s` }} />
        ))}
      </div>

      <motion.div style={{ opacity }} className="relative z-10 mx-auto flex h-full max-w-[1440px] flex-col justify-center px-6 md:px-12">
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.2 }} className="mb-6 flex items-center gap-3">
          <span className="h-px w-12 bg-brand-cyan" />
          <span className="font-mono text-xs uppercase tracking-[0.3em] text-brand-cyan">Engineering-Driven HVAC · {CONTACT.location}</span>
        </motion.div>

        <h1 className="font-display text-5xl font-extrabold leading-[0.92] tracking-tight sm:text-6xl lg:text-8xl">
          {LINES.map((line, i) => (
            <span key={line} className="block overflow-hidden py-1">
              <motion.span custom={i} variants={lineVariant} initial="hidden" animate="show" className="block">
                {i === 1 ? <span className="text-slatex-300">{line}</span> : line}
              </motion.span>
            </span>
          ))}
        </h1>

        <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.9, duration: 0.8 }}
          className="mt-8 max-w-2xl font-body text-base leading-relaxed text-slatex-300 md:text-lg">
          Residential and light commercial heating and cooling service, diagnostics, replacement, and installation across Chicago and the surrounding area.
        </motion.p>

        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.05, duration: 0.8 }}
          className="mt-10 flex flex-wrap items-center gap-3">
          <Link to="/contact" data-testid="hero-request-service" className="group flex items-center gap-2 border border-brand-cyan bg-brand-cyan px-7 py-4 font-mono text-xs uppercase tracking-widest text-ink transition-colors hover:bg-brand-cyan/85">
            Request Service <ArrowRight size={15} weight="bold" className="transition-transform group-hover:translate-x-1" />
          </Link>
          <Link to="/contact" data-testid="hero-installation-quote" className="group flex items-center gap-2 border border-white/20 px-7 py-4 font-mono text-xs uppercase tracking-widest text-white transition-colors hover:border-white/50 hover:bg-white/5">
            <Wrench size={15} /> Get an Installation Quote
          </Link>
          <Link to="/light-commercial-hvac" data-testid="hero-commercial" className="group flex items-center gap-2 px-4 py-4 font-mono text-xs uppercase tracking-widest text-slatex-300 transition-colors hover:text-white">
            <Buildings size={15} /> Commercial HVAC
          </Link>
        </motion.div>
      </motion.div>

    </section>
  );
};

export default Hero;
